import React from "react";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CustomListItem } from "./ListItem";
import moment from "moment-timezone";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { observer } from "mobx-react-lite";
import { formatDate, formatTime } from "../../../common/dateTime";
import { Task } from "../../../services/phpServices";
import { Colors } from "../../../theme";
import { useTranslation } from "react-i18next";

interface TaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  taskDetail: Task | undefined;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = observer(
  ({ open, onClose, taskDetail }) => {
    const [isLightboxOpen, setIsLightboxOpen] = React.useState(false);
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
    const [slides, setSlides] = React.useState<any[]>([]);
    React.useEffect(() => {
      setSlides([]);
      if (taskDetail?.logs && taskDetail.logs.length > 0) {
        const lastLog = taskDetail.logs[taskDetail.logs.length - 1];
        const newSlides =
          lastLog.data_photo
            ?.filter((photo) => !photo.toLowerCase().includes("thumb"))
            .map((photo) => ({ src: photo })) ?? [];
        setSlides(newSlides);
      }
    }, [open, taskDetail]);
    const { t } = useTranslation() as any;

    const openLightbox = (index: number) => {
      setCurrentImageIndex(index);
      setIsLightboxOpen(true);
    };

    const closeLightbox = () => {
      setIsLightboxOpen(false);
    };

    return (
      <>
        <Modal
          open={open}
          aria-labelledby="task-details-modal"
          aria-describedby="task-details-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              backgroundColor: "white",
              borderRadius: "4px",
              boxShadow: 24,
              width: "600px",
              maxHeight: "80vh",
              transform: "t(-50%, -50%)",
              overflow: "hidden",
            }}
          >
            {taskDetail && (
              <>
                {/* Modal Header */}
                <Box
                  sx={{
                    height: "40px",
                    backgroundColor: "#08B0A0",
                    display: "flex",
                    alignItems: "center",
                    borderRadius: "4px 4px 0 0",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="h5"
                    color={Colors.white}
                    sx={{ paddingLeft: "16px" }}
                  >
                    {t("26_00_content").toUpperCase()}
                  </Typography>
                  <IconButton onClick={onClose}>
                    <CloseIcon
                      sx={{ color: Colors.white, width: 24, height: 24 }}
                    />
                  </IconButton>
                </Box>

                {/* Modal Content */}
                <Box
                  sx={{
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    paddingBottom: "20px",
                    overflowY: "auto",
                    maxHeight: "calc(80vh - 40px)",
                  }}
                >
                  {/* Task Info Section */}
                  <Typography
                    style={{
                      marginTop: "20px",
                    }}
                    variant="h5"
                    color={Colors.primary}
                  >
                    {t("26_00_information").toUpperCase()}
                  </Typography>
                  <CustomListItem
                    text={t("26_00_name")}
                    containerStyle={{ marginTop: "8px" }}
                    leftContainerWidth={150}
                    RightComponent={
                      <Typography style={{ fontSize: "14px" }}>
                        {taskDetail.name}
                      </Typography>
                    }
                  />
                  <CustomListItem
                    text={t("26_00_description")}
                    containerStyle={{ marginTop: "8px" }}
                    leftContainerWidth={150}
                    RightComponent={
                      <Typography style={{ fontSize: "14px" }}>
                        {taskDetail.description ? taskDetail.description : "-"}
                      </Typography>
                    }
                  />
                  <CustomListItem
                    text={t("26_00_assigned_to")}
                    containerStyle={{ marginTop: "8px" }}
                    leftContainerWidth={150}
                    RightComponent={
                      <Typography style={{ fontSize: "14px" }}>
                        {taskDetail.employees
                          ?.map((emp) => emp.fullname)
                          .join(", ")}
                      </Typography>
                    }
                  />
                  <CustomListItem
                    text={t("26_00_required")}
                    containerStyle={{ marginTop: "8px" }}
                    leftContainerWidth={150}
                    RightComponent={
                      <Typography style={{ fontSize: "14px" }}>
                        {[
                          taskDetail?.note !== 0 && t("00_06_note"),
                          taskDetail?.photo !== 0 && t("26_00_photo"),
                          (taskDetail?.require_time !== 0 ||
                            taskDetail.require_time != null) &&
                            t("00_04_date_time"),
                        ]
                          .filter(Boolean)
                          .join(", ") || t("03_02_none")}{" "}
                      </Typography>
                    }
                  />
                  {taskDetail?.logs?.length === 1 && (
                    <CustomListItem
                      text={t("26_00_status")}
                      containerStyle={{ marginTop: "8px" }}
                      leftContainerWidth={150}
                      RightComponent={
                        <Typography
                          style={{
                            color:
                              taskDetail.logs[0].status === "UNCOMPLETED"
                                ? Colors.warning
                                : Colors.primary,
                            fontSize: "14px",
                          }}
                        >
                          {taskDetail.logs[0].status === "UNCOMPLETED"
                            ? t("26_00_incompleted").toUpperCase()
                            : taskDetail.logs[0].status === "CREATED"
                            ? t("26_00_created")
                            : t("26_00_completed")}
                        </Typography>
                      }
                    />
                  )}

                  {/* Task Logs Section */}
                  {taskDetail?.logs && taskDetail.logs.length > 1 && (
                    <>
                      <Box
                        style={{
                          width: "calc(100% + 40px)",
                          borderBottom: "6px solid #E0E0E0",
                          marginTop: "24px",
                          marginBottom: "10px",
                          marginLeft: "-20px",
                          marginRight: "-40px",
                        }}
                      />
                      <Typography
                        style={{
                          marginTop: "20px",
                          marginBottom: "8px",
                        }}
                        color={Colors.primary}
                        variant="h5"
                      >
                        {t("26_00_performance").toUpperCase()}
                      </Typography>

                      {/* lấy log cuối cùng */}
                      {(() => {
                        const log = taskDetail.logs[taskDetail.logs.length - 1];
                        return (
                          <>
                            <CustomListItem
                              text={t("26_00_status")}
                              leftContainerWidth={150}
                              RightComponent={
                                <Typography
                                  style={{
                                    color:
                                      log.status === "COMPLETED"
                                        ? Colors.primary
                                        : Colors.warning,
                                    fontSize: "14px",
                                  }}
                                >
                                  {log.status}
                                </Typography>
                              }
                            />
                            <CustomListItem
                              text={t("00_04_date")}
                              containerStyle={{ marginTop: "8px" }}
                              leftContainerWidth={150}
                              RightComponent={
                                <Typography style={{ fontSize: "14px" }}>
                                  {moment
                                    .unix(Number(log.task_date))
                                    .format("MM/DD/YYYY")}
                                </Typography>
                              }
                            />
                            <CustomListItem
                              text={t("26_00_updated")}
                              containerStyle={{ marginTop: "8px" }}
                              leftContainerWidth={150}
                              RightComponent={
                                <Typography style={{ fontSize: "14px" }}>
                                  {log.created_employee?.fullname}
                                </Typography>
                              }
                            />
                            {log.data_note && (
                              <CustomListItem
                                text={t("00_06_note")}
                                containerStyle={{ marginTop: "8px" }}
                                leftContainerWidth={150}
                                RightComponent={
                                  <Typography style={{ fontSize: "14px" }}>
                                    "{log.data_note}"
                                  </Typography>
                                }
                              />
                            )}
                            {Number(log.created_at) && (
                              <CustomListItem
                                text={t("00_04_date_time")}
                                containerStyle={{ marginTop: "8px" }}
                                leftContainerWidth={150}
                                RightComponent={
                                  <Typography
                                    style={
                                      taskDetail?.require_time !== null &&
                                      (log?.created_at ?? 0) >
                                        (taskDetail?.require_time ?? 0) * 60 +
                                          (taskDetail?.date_instance ?? 0)
                                        ? {
                                            color: Colors.danger,
                                            fontSize: "14px",
                                          }
                                        : {
                                            color: Colors.black,
                                            fontSize: "14px",
                                          }
                                    }
                                  >
                                    {formatDate(
                                      Number(taskDetail?.date_instance),
                                      "DD MMMM YYYY"
                                    )}{" "}
                                    @{formatTime(log?.created_at ?? 0)}
                                  </Typography>
                                }
                              />
                            )}
                            {log.data_photo && log.data_photo.length > 0 && (
                              <>
                                <Typography
                                  style={{
                                    marginTop: "16px",
                                    color: Colors.black,
                                    fontSize: "14px",
                                  }}
                                >
                                  {t("26_00_photo")}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "16px",
                                    flexDirection: "row",
                                    marginTop: "12px",
                                  }}
                                >
                                  {log.data_photo
                                    .filter(
                                      (photo) =>
                                        !photo.toLowerCase().includes("thumb")
                                    )
                                    .map((photo, subIndex) => (
                                      <img
                                        key={subIndex}
                                        src={photo}
                                        alt={`task-photo-${subIndex}`}
                                        onClick={() => openLightbox(subIndex)}
                                        style={{
                                          width: "100px",
                                          height: "100px",
                                          borderRadius: "4px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                      />
                                    ))}
                                </Box>
                              </>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Modal>
        {isLightboxOpen && (
          <Lightbox
            open={isLightboxOpen}
            close={closeLightbox}
            slides={slides ?? []}
            index={currentImageIndex}
            render={{
              buttonPrev: slides.length > 1 ? undefined : () => null,
              buttonNext: slides.length > 1 ? undefined : () => null,
            }}
          />
        )}
      </>
    );
  }
);

export default TaskDetailModal;
